
import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from 'react-router-dom';

// import axios from "axios";

// @mui material components
import Grid from "@mui/material/Grid";
import { Card, CardHeader, CardContent, Button, Tabs,Box, Tab, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Snackbar, Alert, Backdrop, CircularProgress, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
// import {

//   Table,
//   TableRow,
//   TableCell,
//   TablePagination,
//   Box,
// } from "@mui/material";
// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import {
  ProfileInfoCard,
  DriverOrdersHistory,
  DriverTransactionsHistory,
  VehicleHistory,
} from "features/drivers/components";


// API
import { fetchDriverById, fetchCoa as fetchCoaApi, createOrAddAmount, getWalletByUserId, getTransactionsByWalletId, } from "features/drivers/services/driversService";

function Overview() {
  const [driver, setDriver] = useState({});
  const [coas, setCoas] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const { id } = useParams(); // Extract 'id' from URL
  const navigate = useNavigate();
  const [currentId] = useState(id);
  const [formData, setFormData] = useState({ amount: "", coa: "", description: "" });
  const [formErrors, setFormErrors] = useState({ amount: "", coa: "" });
  const [walletData, setWalletData] = useState(null);
  const [walletTransactions, setWalletTransactions] = useState([]);


  // Tabs for activity
  const [tab, setTab] = useState(0);
  const handleTabChange = (_e, v) => setTab(v);

  // Add amount dialog
  const [addOpen, setAddOpen] = useState(false);
  const isAddValid = (() => {
    const amount = parseFloat(formData.amount);
    const amountOk = !isNaN(amount) && amount > 0;
    const coaOk = coas && coas.length > 0 ? Boolean(formData.coa) : true;
    return amountOk && coaOk;
  })();
  const validateForm = () => {
    const errs = { amount: "", coa: "" };
    const amount = parseFloat(formData.amount);
    if (isNaN(amount) || amount <= 0) errs.amount = "Enter a valid amount > 0";
    if (coas && coas.length > 0 && !formData.coa) errs.coa = "Please select a COA";
    setFormErrors(errs);
    return !errs.amount && !errs.coa;
  };
  const handleAddConfirm = async () => {
    if (!validateForm()) return;
    const val = parseFloat(formData.amount);
    if (!isNaN(val) && val > 0) {
      try {
        setLoading(true);
        const data = {
          user_id: driver.id,
          balance: val,
          type: formData.coa,
          description: formData.description,
        };

        const response = await createOrAddAmount(data);
        if (response?.success) {
          setSuccess("Wallet updated successfully.");
          await fetchWallet();
        } else {
          setError(response?.message || "Failed to update wallet.");
        }
      } catch (e) {
        setError("Failed to update wallet.");
      } finally {
        setLoading(false);
        handleCloseAddDialog();
      }
    }
  };

  const handleCloseAddDialog = () => {
    setAddOpen(false);
    setFormData({ amount: "", coa: "", description: "" });
    setFormErrors({ amount: "", coa: "" });
  };

  const fetchDriver = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchDriverById(currentId);
      const data = response?.data ?? response;
      if (data) setDriver(data);
    } catch (error) {
      setError("Failed to fetch driver. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [currentId]);

  const fetchWallet = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getWalletByUserId(currentId);
      if (response.success) {
        const data = response?.data;
        if (data) setWalletData(data);
      }
    } catch (error) {
      setError("Failed to fetch driver. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [currentId]);

  const fetchWalletTransactions = useCallback(async (walletId) => {
    if (!walletId) return;
    setLoading(true);
    try {
      const response = await getTransactionsByWalletId({ walletId, page: 1, total: 100 });
      // Normalize items from common shapes
      const payload = response?.data ?? response;
      const items = Array.isArray(payload)
        ? payload
        : payload?.items || payload?.transactions || [];

      const mapped = (items || []).map((it) => ({
        id: it.id,
        date: it.createdAt || it.created_at || it.date || null,
        type: String(it.type || "").toLowerCase(),
        amount: it.amount ?? 0,
        desc: it.description || it.desc || "",
      }));
      setWalletTransactions(mapped);
    } catch (error) {
      setError("Failed to fetch wallet transactions. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCoas = useCallback(async () => {
    try {
      const response = await fetchCoaApi();
      const list = response?.data || [];
      const filtered = (list || [])
        .filter((item) => {
          const v = String(item ?? "").toLowerCase();
          return v === "credit" || v === "debit";
        })
        .map((item) => {
          const v = String(item ?? "").toLowerCase();
          return v === "credit" ? "Credit" : v === "debit" ? "Debit" : item;
        });
      setCoas(filtered);
    } catch (error) {
      console.error("Error fetching COAs:", error);
      setError("Failed to fetch COAs. Please try again.");
    } finally {
    }
  }, []);

 
  useEffect(() => {
    if (currentId) {
      fetchDriver();
      loadCoas();
      fetchWallet();
      // fetchDriverPayout();
    }
  }, [currentId, fetchDriver, loadCoas, fetchWallet]);

  useEffect(() => {
    const walletId = walletData?.id || walletData?.wallet_id || walletData?.walletId;
    if (walletId) {
      fetchWalletTransactions(walletId);
    }
  }, [walletData, fetchWalletTransactions]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const sanitizeAmount = (raw) => {
    if (typeof raw !== 'string') raw = String(raw ?? '');
    let v = raw.replace(/[^0-9.]/g, '');
    const firstDot = v.indexOf('.');
    if (firstDot !== -1) {
      // remove all extra dots
      v = v.slice(0, firstDot + 1) + v.slice(firstDot + 1).replace(/\./g, '');
    }
    return v;
  };




  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={3} pb={3} px={2}>
        <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <MDTypography variant="h5">Driver Detail</MDTypography>
          <Button variant="outlined" style={{ color: "black", borderColor: "black" }} onClick={() => navigate(-1)}>Back</Button>
        </MDBox>

        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <ProfileInfoCard
              title="Profile Information"
              description=""
              info={{
                fullName: driver.full_name || "",
                mobile: driver.contact || "",
                email: driver.email || "",
                location: driver.address || "",
              }}
              shadow={false}
            />

            <MDBox mt={2} />
            <Card>
              <CardHeader
                title="Wallet Summary"
                action={<Button variant="contained" size="small" style={{ color: "white" }} onClick={() => setAddOpen(true)}>Add/Remove Amount</Button>}
              />
              <Box className="flex flex-col">
                {walletData ? (
                  <>
                    <Box sx={{ padding: "10px", margin: "10px"}}>
                      <MDTypography variant="button"><strong>Balance:</strong> QAR {walletData.balance.toFixed(2)}</MDTypography><br />
                      <MDTypography variant="button"><strong>Previous Balance:</strong> QAR {(walletData.previous_balance)}</MDTypography><br />
                      <MDTypography variant="button">
                        <strong>Last Transaction: </strong> QAR
                        <span
                          style={{
                            color:
                              walletData.transactions[0]?.type === "Debit" ? "red" : "green"
                          }}
                        >
                          {" "}
                          {walletData.transactions[0]?.amount || 0}{" "}
                          {walletData.transactions[0]?.type}
                        </span>
                      </MDTypography>

                    </Box>
                  </>
                ) : (
                  <MDTypography variant="button">No wallet found</MDTypography>
                )}
              </Box>
            </Card>
          </Grid>

          <Grid item xs={12} md={8}>
            <Card>
              <CardHeader title="Activity" />
              <Tabs value={tab} onChange={handleTabChange} sx={{ px: 2 }}>
                <Tab label="Vehicles" />
                <Tab label="Orders" />
                <Tab label="Wallet Transactions" />
              </Tabs>
              <MDBox p={2}>
                {tab === 0 && <VehicleHistory vehicles={driver.vehicles || []} />}
                {tab === 1 && <DriverOrdersHistory currentId={currentId} />}
                {tab === 2 && <DriverTransactionsHistory transactions={walletTransactions} />}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
       

      </MDBox>
      <Footer />
      {/* Add Amount Dialog */}
      <Dialog open={addOpen} onClose={handleCloseAddDialog}>
        <DialogTitle>Add/Remove Amount to Wallet</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1, mb: 1 }}>
            <InputLabel id="coa-select-label">Chart of Account</InputLabel>
            <Select style={{ height: '40px' }}
              labelId="coa-select-label"
              label="Chart of Account"
              name="coa"
              value={formData.coa}
              onChange={handleInputChange}
            >
              {coas && coas.length > 0 ? (
                coas.map((item, idx) => {
                  const label = item;
                  const value = item;
                  return (
                    <MenuItem key={value || idx} value={value}>
                      {label}
                    </MenuItem>
                  );
                })
              ) : (
                <MenuItem value="" disabled>
                  {coas ? 'No COAs available' : 'Loading COAs...'}
                </MenuItem>
              )}
            </Select>
            {formErrors.coa ? (
              <MDTypography variant="caption" color="error">{formErrors.coa}</MDTypography>
            ) : null}
          </FormControl>
          <TextField
            autoFocus
            margin="dense"
            label="Amount"
            type="text"
            fullWidth
            name="amount"
            value={formData.amount}
            inputProps={{ inputMode: 'decimal', pattern: '[0-9]*[.]?[0-9]*' }}
            error={Boolean(formErrors.amount)}
            helperText={formErrors.amount}
            onKeyDown={(event) => {
              const allowed = [
                'Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End'
              ];
              const isNumber = /[0-9]/.test(event.key);
              const isDot = event.key === '.';
              if (allowed.includes(event.key)) return;
              if (!(isNumber || isDot)) {
                event.preventDefault();
              }
            }}
            onChange={(event) => {
              const { value } = event.target;
              const next = sanitizeAmount(value);
              handleInputChange({ target: { name: 'amount', value: next } });
            }}
          />
          <TextField
            fullWidth
            label="Description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            margin="normal"
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddDialog}>Cancel</Button>
          <Button variant="contained" style={{ color: 'white' }} onClick={handleAddConfirm} disabled={!isAddValid}>Submit</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={!!error || !!success}
        autoHideDuration={3000}
        onClose={() => { setError(null); setSuccess(null); }}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => { setError(null); setSuccess(null); }} severity={error ? "error" : "success"} sx={{ width: '100%' }}>
          {error || success}
        </Alert>
      </Snackbar>

      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

    </DashboardLayout>
  );
}

export default Overview;
